import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Shield, RefreshCw, CheckCircle, Heart, Lock, TrendingUp } from 'lucide-react'
import { usePageMeta } from '@/hooks/usePageMeta'

export default function ForDonors() {
  const navigate = useNavigate()
  usePageMeta({
    title: 'For Donors | Claritas',
    description:
      'Give to your parish effortlessly. Enable automatic round-ups on everyday purchases and support your Catholic faith community—free for donors, no hidden fees.',
  })

  return (
    <div className="min-h-screen bg-background-light">

      {/* Hero */}
      <section className="bg-white py-20 border-b border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            For Donors
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-charcoal mb-6">
            Give Without Thinking Twice
          </h1>
          <p className="text-base text-charcoal-muted max-w-2xl mx-auto mb-8">
            Claritas makes it effortless to support your parish. Connect your bank, enable automatic
            round-ups, and turn everyday purchases into meaningful contributions to your faith community.
          </p>
          <Button size="lg" onClick={() => navigate('/sign-in')} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
            Start Donating
          </Button>
        </div>
      </section>

      {/* Features */}
      <section className="bg-background-light py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              Features
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
              Giving Made Simple
            </h2>
            <p className="text-base text-charcoal-muted max-w-xl mx-auto">
              Everything you need to make consistent, secure contributions to your parish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <RefreshCw className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Automatic Round-Ups</h3>
              <p className="text-sm text-charcoal-muted">
                Every purchase is rounded up to the nearest dollar. The spare change accumulates and is
                donated to your chosen parish automatically.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <CheckCircle className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">One-Time Donations</h3>
              <p className="text-sm text-charcoal-muted">
                Make a direct contribution anytime. Whether it's a special feast day, a capital campaign,
                or a moment of gratitude—give on your terms.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Shield className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Bank-Level Security</h3>
              <p className="text-sm text-charcoal-muted">
                Your financial data is protected by Stripe—the same security infrastructure
                used by leading financial institutions worldwide.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <TrendingUp className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Giving Dashboard</h3>
              <p className="text-sm text-charcoal-muted">
                Track your total contributions over time. See the cumulative impact of your giving and
                stay connected to how your parish uses the funds.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Heart className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Parish Directory</h3>
              <p className="text-sm text-charcoal-muted">
                Browse registered parishes and choose the community you want to support. Change your
                designated parish anytime from your account.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Lock className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Full Control</h3>
              <p className="text-sm text-charcoal-muted">
                Pause, resume, or cancel round-ups at any time. You remain in complete control of your
                giving with no hidden commitments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Brief */}
      <section className="bg-white py-20 border-t border-surface-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              Getting Started
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
              Three Steps to Begin
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                1
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Create an Account</h3>
              <p className="text-sm text-charcoal-muted">
                Sign up with your email and choose your parish from our directory
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                2
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Link Your Bank</h3>
              <p className="text-sm text-charcoal-muted">
                Connect securely via Stripe in under two minutes
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                3
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Enable Round-Ups</h3>
              <p className="text-sm text-charcoal-muted">
                Turn on automatic giving and watch your contributions grow
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-light py-20 border-t border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Join Today
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
            Every Transaction, an Act of Faith
          </h2>
          <p className="text-base text-charcoal-muted mb-8 max-w-xl mx-auto">
            Free to use for donors. No subscription fees, no hidden charges. Just meaningful giving.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" onClick={() => navigate('/sign-in')} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
              Get Started Free
            </Button>
            <Button variant="ghost" size="lg" onClick={() => navigate('/how-it-works')}>
              How It Works
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
